import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import LaboratoryOrderService from '../../services/laboratoryOrderService';
import ProviderService from '../../services/providerService';
import Button from '../common/Button';
import LaboratoryOrdersView from '../laboratory/LaboratoryOrdersView';
import {
  MANUAL_STATUS_OPTIONS,
  formatLaboratoryDate,
  getDisplayStatus
} from '../laboratory/laboratoryOrderUtils';
import '../laboratory/LaboratoryOrders.css';
import './ProviderDetails.css';

const ProviderDetails = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { timezone } = useOrganizationTimezone();

  const [provider, setProvider] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [uploadingOrderId, setUploadingOrderId] = useState(null);

  const loadProvider = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = auth.user?.access_token;
      const data = await ProviderService.getProviderById(providerId, token);
      setProvider(data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al cargar el proveedor' });
    } finally {
      setIsLoading(false);
    }
  }, [auth.user?.access_token, providerId]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    loadProvider();
  }, [auth.isAuthenticated, loadProvider]);

  useEffect(() => {
    if (location.state?.message) {
      setMessage({ type: 'success', text: location.state.message });
    }
  }, [location.state]);

  const replaceOrderInState = useCallback((updatedOrder) => {
    if (!updatedOrder?.id) {
      return;
    }

    setOrders((currentOrders) => currentOrders.map((item) => (
      item.id === updatedOrder.id ? { ...item, ...updatedOrder } : item
    )));
  }, []);

  const loadOrders = useCallback(async () => {
    const token = auth.user?.access_token;

    if (!token || !providerId) {
      return;
    }

    try {
      setIsOrdersLoading(true);
      setOrdersError('');
      const orderItems = await LaboratoryOrderService.getOrdersByProviderAllPages(providerId, token);
      const sortedOrders = [...orderItems].sort((left, right) => new Date(right.createdAt) - new Date(left.createdAt));
      setOrders(sortedOrders);
    } catch (error) {
      setOrdersError('No se pudieron cargar los trabajos de laboratorio de este proveedor');
    } finally {
      setIsOrdersLoading(false);
    }
  }, [auth.user?.access_token, providerId]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    loadOrders();
  }, [auth.isAuthenticated, loadOrders]);

  const contactItems = useMemo(() => {
    if (!provider) return [];
    return [
      { label: 'Contacto', value: provider.contactName || '-' },
      { label: 'Telefono', value: provider.phone || '-' },
      { label: 'Email', value: provider.email || '-' }
    ];
  }, [provider]);

  const serviceItems = useMemo(() => {
    if (!provider) return [];
    return [
      {
        label: 'Entrega Promedio',
        value: provider.deliveryTimeDays !== null && provider.deliveryTimeDays !== undefined
          ? `${provider.deliveryTimeDays} dias`
          : '-'
      },
      { label: 'Direccion', value: provider.address || '-' }
    ];
  }, [provider]);

  const handleBack = () => {
    navigate('/laboratory-orders/providers');
  };

  const handleEdit = () => {
    navigate(`/laboratory-orders/providers/${providerId}/edit`);
  };

  const handleDelete = async () => {
    if (!provider) return;
    setIsDeleting(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      await ProviderService.deleteProvider(providerId, token);
      navigate('/laboratory-orders/providers', { state: { message: 'Proveedor eliminado exitosamente' } });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al eliminar el proveedor' });
      setIsDeleting(false);
    }
  };

  const handleOpenOrder = useCallback((order) => {
    if (!order?.id) {
      return;
    }

    navigate(`/laboratory-orders/${order.id}/edit`);
  }, [navigate]);

  const handleStatusChange = useCallback(async (orderId, nextStatus) => {
    const token = auth.user?.access_token;
    const order = orders.find((item) => item.id === orderId);

    if (!token || !order || !nextStatus || nextStatus === order.status) {
      return;
    }

    const previousOrder = { ...order };

    try {
      replaceOrderInState({ ...order, status: nextStatus });
      const updatedOrder = await LaboratoryOrderService.updateOrderStatus(
        orderId,
        {
          status: nextStatus,
          note: nextStatus === 'rework'
            ? 'Pieza marcada para ajuste'
            : nextStatus === 'cancelled'
              ? 'Orden cancelada desde la vista del proveedor'
              : ''
        },
        token
      );
      replaceOrderInState(updatedOrder);
    } catch (error) {
      replaceOrderInState(previousOrder);
      setOrdersError(error.message || 'No se pudo actualizar el estado de la orden');
    }
  }, [auth.user?.access_token, orders, replaceOrderInState]);

  const handleCancelOrder = useCallback(async (order) => {
    if (order.status === 'cancelled') {
      return;
    }

    const confirmed = window.confirm(`¿Cancelar la orden de ${order.patientName || 'este paciente'}?`);
    if (!confirmed) {
      return;
    }

    await handleStatusChange(order.id, 'cancelled');
  }, [handleStatusChange]);

  const handleOrderFileUpload = useCallback(async (orderId, event) => {
    const token = auth.user?.access_token;
    const filesToUpload = Array.from(event.target.files || []);
    event.target.value = '';

    if (!token || !filesToUpload.length) {
      return;
    }

    try {
      setUploadingOrderId(orderId);
      setOrdersError('');

      for (const file of filesToUpload) {
        const contentType = file.type || 'application/octet-stream';
        const uploadData = await LaboratoryOrderService.getAttachmentUploadUrl(
          orderId,
          { fileName: file.name, contentType },
          token
        );
        await LaboratoryOrderService.uploadAttachmentFile(uploadData.uploadUrl, file, contentType);
        await LaboratoryOrderService.attachFile(
          orderId,
          {
            attachmentId: uploadData.attachmentId,
            fileName: file.name,
            contentType,
            key: uploadData.key
          },
          token
        );
      }

      await loadOrders();
    } catch (error) {
      setOrdersError(error.message || 'No se pudieron cargar los adjuntos');
    } finally {
      setUploadingOrderId(null);
    }
  }, [auth.user?.access_token, loadOrders]);

  const laboratoryOrdersContent = useMemo(() => {
    if (isOrdersLoading) {
      return (
        <div className="provider-laboratory-state provider-laboratory-loading">
          <div className="loading-spinner"></div>
        </div>
      );
    }

    if (ordersError) {
      return (
        <div className="provider-laboratory-state provider-laboratory-error">
          <div>
            <h3>No se pudieron cargar los trabajos</h3>
            <p>{ordersError}</p>
          </div>
          <Button variant="outline" onClick={loadOrders}>
            Reintentar
          </Button>
        </div>
      );
    }

    if (orders.length === 0) {
      return (
        <div className="provider-laboratory-state">
          <div>
            <h3>Sin trabajos registrados</h3>
            <p>Este proveedor todavia no tiene ordenes de laboratorio asignadas.</p>
          </div>
          <Button
            variant="outline"
            icon="fas fa-plus"
            onClick={() => navigate('/laboratory-orders/new')}
          >
            Nueva Orden
          </Button>
        </div>
      );
    }

    return (
      <LaboratoryOrdersView
        orders={orders}
        timezone={timezone}
        manualStatusOptions={MANUAL_STATUS_OPTIONS}
        getDisplayStatus={getDisplayStatus}
        formatLaboratoryDate={formatLaboratoryDate}
        onEdit={handleOpenOrder}
        onStatusChange={handleStatusChange}
        onCancel={handleCancelOrder}
        onUpload={handleOrderFileUpload}
        uploadingOrderId={uploadingOrderId}
      />
    );
  }, [
    handleCancelOrder,
    handleOpenOrder,
    handleOrderFileUpload,
    handleStatusChange,
    isOrdersLoading,
    loadOrders,
    navigate,
    orders,
    ordersError,
    timezone,
    uploadingOrderId
  ]);

  if (isLoading) {
    return (
      <div className="provider-details-container loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="provider-details-container not-found">
        <h2>Proveedor no encontrado</h2>
        <Button onClick={handleBack} variant="secondary" className="back-btn">
          <i className="fas fa-arrow-left"></i> Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="provider-details-container">
      <div className="back-button-section">
        <Button
          variant="outline"
          onClick={handleBack}
          icon="fas fa-arrow-left"
        >
          Regresar
        </Button>
        <div className="provider-header-actions">
          <Button
            variant="outline"
            onClick={handleEdit}
            icon="fas fa-edit"
          >
            Editar
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowDeleteConfirm(prev => !prev)}
            icon="fas fa-trash"
            disabled={isDeleting}
          >
            Eliminar
          </Button>
        </div>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="provider-profile-header">
        <div className="provider-avatar">
          {provider.name?.charAt(0).toUpperCase()}
        </div>
        <div className="provider-profile-info">
          <h1>{provider.name}</h1>
          <div className="provider-info-sections">
            <div className="info-section">
              <h3>Contacto</h3>
              {contactItems.map(item => (
                <div key={item.label} className="info-row">
                  <span className="label">{item.label}</span>
                  <span className="value">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="info-section">
              <h3>Servicios y entrega</h3>
              {serviceItems.map(item => (
                <div key={item.label} className="info-row">
                  <span className="label">{item.label}</span>
                  <span className="value">{item.value}</span>
                </div>
              ))}
              <div className="info-row full-width">
                <span className="label">Servicios</span>
                <span className="value">
                  {(provider.services || []).length > 0
                    ? provider.services.join(', ')
                    : '-'}
                </span>
              </div>
            </div>
            <div className="info-section full-width">
              <h3>Notas</h3>
              <p className="notes-text">{provider.notes || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="provider-details-placeholder">
        <div className="placeholder-header">
          <div>
            <h2>Trabajos</h2>
            <p>
              Revisa el estado de cada orden asignada a este proveedor.
            </p>
          </div>
        </div>
        {laboratoryOrdersContent}
      </div>

      {showDeleteConfirm && (
        <div className="provider-danger-zone">
          <h3>Zona de Precaucion</h3>
          <p>Eliminar este proveedor quitara el registro del directorio.</p>
          <div className="danger-actions">
            <button className="btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </button>
            <button className="btn-danger" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <span className="button-spinner" aria-hidden="true"></span> : 'Confirmar Eliminacion'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderDetails;
